import { useState, useEffect, useCallback } from 'react';

const HELIUS_API = 'https://api.helius.xyz/v0';
const LAMPORTS   = 1e9;

function getHeliusKey() {
  return localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
}

function timeAgo(ms) {
  const diff = Date.now() - ms;
  if (diff < 60000)   return Math.floor(diff / 1000) + 's';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'min';
  return Math.floor(diff / 3600000) + 'h';
}

function fmtUsd(n) {
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000)    return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + n.toFixed(0);
}

async function fetchNewTokens(minLiq, maxAgeHours) {
  try {
    // Récupérer depuis 3 sources en parallèle
    const [r1, r2] = await Promise.allSettled([
      fetch('https://api.dexscreener.com/token-profiles/latest/v1'),
      fetch('https://api.dexscreener.com/token-boosts/latest/v1'),
    ]);

    const addrSet = new Set();

    for (const res of [r1, r2]) {
      if (res.status !== 'fulfilled' || !res.value.ok) continue;
      const data = await res.value.json();
      (Array.isArray(data) ? data : [])
        .filter(p => p.chainId === 'solana' && p.tokenAddress)
        .forEach(p => addrSet.add(p.tokenAddress));
    }

    if (!addrSet.size) return [];

    // Découper en lots de 30 (limite DexScreener) et fetch en parallèle
    const addrs  = [...addrSet];
    const chunks = [];
    for (let i = 0; i < addrs.length; i += 30) chunks.push(addrs.slice(i, i + 30));

    const results = await Promise.allSettled(
      chunks.map(chunk =>
        fetch('https://api.dexscreener.com/latest/dex/tokens/' + chunk.join(',')).then(r => {
          if (r.status === 429) throw new Error('RATE_LIMIT');
          return r.json();
        })
      )
    );

    if (results.some(r => r.status === 'rejected' && r.reason?.message === 'RATE_LIMIT')) {
      throw new Error('RATE_LIMIT');
    }

    const seen  = new Set();
    const pairs = [];
    for (const res of results) {
      if (res.status !== 'fulfilled') continue;
      for (const p of (res.value.pairs || [])) {
        if (p.chainId !== 'solana') continue;
        if (seen.has(p.pairAddress)) continue;
        seen.add(p.pairAddress);
        if (parseFloat(p.liquidity?.usd || 0) >= minLiq) pairs.push(p);
      }
    }

    const cutoff = Date.now() - maxAgeHours * 60 * 60 * 1000;
    return pairs
      .filter(p => p.pairCreatedAt && p.pairCreatedAt >= cutoff)
      .sort((a, b) => parseFloat(b.liquidity?.usd || 0) - parseFloat(a.liquidity?.usd || 0))
      .slice(0, 100);
  } catch(e) {
    if (e.message === 'RATE_LIMIT') throw e;
    console.error('fetchNewTokens error:', e.message);
    return [];
  }
}

async function fetchSolPrice() {
  try {
    const r = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT');
    const j = await r.json();
    return parseFloat(j.price) || 150;
  } catch { return 150; }
}

async function fetchBigBuyers(mint, minSol) {
  const key = getHeliusKey();
  if (!key) throw new Error('Clé Helius manquante — configure-la dans le Panel Admin');
  const r = await fetch(`${HELIUS_API}/addresses/${mint}/transactions?api-key=${key}&type=SWAP&limit=100`);
  if (r.status === 429) throw new Error('Limite API atteinte — attends quelques secondes');
  if (!r.ok) throw new Error('Erreur API Helius (' + r.status + ')');
  const txns = await r.json();
  if (!Array.isArray(txns)) throw new Error('Réponse invalide');

  const buyerMap = {};
  for (const tx of txns) {
    for (const tt of (tx.tokenTransfers || [])) {
      if (tt.mint !== mint || parseFloat(tt.tokenAmount || 0) <= 0) continue;
      const wallet = tt.toUserAccount;
      if (!wallet) continue;
      let solSpent = 0;
      for (const nt of (tx.nativeTransfers || [])) {
        if (nt.fromUserAccount === wallet) solSpent += nt.amount / LAMPORTS;
      }
      if (solSpent < minSol) continue;
      if (!buyerMap[wallet]) buyerMap[wallet] = { wallet, totalSol: 0, trades: 0, firstSeen: tx.timestamp };
      buyerMap[wallet].totalSol += solSpent;
      buyerMap[wallet].trades  += 1;
      if (tx.timestamp < buyerMap[wallet].firstSeen) buyerMap[wallet].firstSeen = tx.timestamp;
    }
  }
  return Object.values(buyerMap).sort((a, b) => b.totalSol - a.totalSol);
}

const S = {
  root:       { display:'flex', height:'calc(100vh - 52px)', overflow:'hidden', background:'#060809' },
  left:       { width:400, borderRight:'1px solid #1a2030', display:'flex', flexDirection:'column', overflow:'hidden', flexShrink:0 },
  right:      { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  topBar:     { padding:'12px 16px', borderBottom:'1px solid #1a2030', display:'flex', alignItems:'center', gap:8, flexShrink:0 },
  filterBar:  { padding:'8px 16px', borderBottom:'1px solid #1a2030', display:'flex', gap:8, alignItems:'center', flexShrink:0 },
  list:       { flex:1, overflowY:'auto' },
  emptyState: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#3a4a5a', gap:10 },
  inp:        { background:'#0d1117', border:'1px solid #1a2030', borderRadius:5, color:'#e0e8f0', fontSize:11, padding:'5px 9px', outline:'none' },
  btn:        (color) => ({ background:'rgba('+color+',0.08)', border:'1px solid rgba('+color+',0.2)', borderRadius:6, color:'rgb('+color+')', fontSize:11, padding:'4px 10px', cursor:'pointer', fontWeight:600 }),
};

export default function SnipersTab({ onAnalyzeWallet }) {
  const [tokens,       setTokens]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [minLiq,       setMinLiq]       = useState(5000);
  const [minSol,       setMinSol]       = useState(1);
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [buyers,       setBuyers]       = useState([]);
  const [buyLoad,      setBuyLoad]      = useState(false);
  const [buyError,     setBuyError]     = useState('');
  const [copied,       setCopied]       = useState('');
  const [maxAge,       setMaxAge]       = useState(2); // heures
  const [solPrice,     setSolPrice]     = useState(150);
  const [fetchError,   setFetchError]   = useState('');

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await fetchNewTokens(minLiq, maxAge);
      setTokens(data);
      setLastRefresh(Date.now());
    } catch(e) {
      if (e.message === 'RATE_LIMIT') setFetchError('Limite DexScreener atteinte — réessai automatique dans 30s');
    }
    setLoading(false);
  }, [minLiq, maxAge]);

  useEffect(() => {
    fetchSolPrice().then(p => setSolPrice(p));
  }, []);

  useEffect(() => {
    loadTokens();
    const t = setInterval(loadTokens, 30000);
    return () => clearInterval(t);
  }, [loadTokens]);

  const selectToken = (token) => {
    setSelected(token);
    setBuyers([]);
    setBuyError('');
    setBuyLoad(false);
  };

  const loadBuyers = async () => {
    if (!selected) return;
    setBuyers([]);
    setBuyError('');
    setBuyLoad(true);
    try {
      const data = await fetchBigBuyers(selected.baseToken.address, minSol);
      setBuyers(data);
    } catch(e) { setBuyError(e.message); }
    setBuyLoad(false);
  };

  const copyAddr = (addr) => {
    navigator.clipboard.writeText(addr);
    setCopied(addr);
    setTimeout(() => setCopied(''), 1500);
  };

  const filtered = tokens.filter(t =>
    !search ||
    (t.baseToken?.symbol || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.baseToken?.name   || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={S.root}>

      {/* ── COLONNE GAUCHE : liste des tokens ── */}
      <div style={S.left}>

        <div style={S.topBar}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:'#e0e8f0'}}>🎯 SNIPERS</div>
            <div style={{fontSize:10,color:fetchError?'#f59e0b':'#3a4a5a',marginTop:1}}>
              {loading ? 'Chargement...' : fetchError ? fetchError : `${filtered.length} tokens · ${lastRefresh ? 'Dernière MAJ ' + timeAgo(lastRefresh) : ''}`}
            </div>
          </div>
          <button onClick={loadTokens} disabled={loading} style={S.btn('0,229,204')}>
            ↻ Refresh
          </button>
        </div>

        <div style={S.filterBar}>
          <input
            placeholder="Rechercher un token..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...S.inp, flex:1}}
          />
          <span style={{fontSize:10,color:'#5a6a80',whiteSpace:'nowrap'}}>Liq min</span>
          <input
            type="number" value={minLiq}
            onChange={e => setMinLiq(Number(e.target.value))}
            style={{...S.inp, width:60}}
          />
          <span style={{fontSize:10,color:'#5a6a80'}}>$</span>
          <select
            value={maxAge}
            onChange={e => setMaxAge(Number(e.target.value))}
            style={{...S.inp, width:70, padding:'4px 6px', cursor:'pointer'}}
          >
            <option value={1}>1h</option>
            <option value={2}>2h</option>
            <option value={6}>6h</option>
            <option value={24}>24h</option>
          </select>
        </div>

        <div style={S.list}>
          {loading && !tokens.length ? (
            <div style={{padding:32,textAlign:'center',color:'#3a4a5a',fontSize:12}}>Chargement en cours...</div>
          ) : filtered.length === 0 ? (
            <div style={{padding:32,textAlign:'center',color:'#3a4a5a',fontSize:12}}>Aucun token trouvé<br/><span style={{fontSize:10}}>Essaie de baisser la liquidité minimale</span></div>
          ) : filtered.map((t, i) => {
            const isSel      = selected?.pairAddress === t.pairAddress;
            const liq        = parseFloat(t.liquidity?.usd  || 0);
            const vol        = parseFloat(t.volume?.h24      || 0);
            const chg1h      = parseFloat(t.priceChange?.h1  || 0);
            const buys24h    = t.txns?.h24?.buys  || 0;
            const sells24h   = t.txns?.h24?.sells || 0;
            const age        = t.pairCreatedAt ? timeAgo(t.pairCreatedAt) : '?';

            return (
              <div
                key={t.pairAddress || i}
                onClick={() => selectToken(t)}
                style={{
                  padding:'10px 16px', borderBottom:'1px solid #0d1117',
                  cursor:'pointer',
                  background: isSel ? 'rgba(0,229,204,0.04)' : 'transparent',
                  borderLeft: isSel ? '2px solid #00e5cc' : '2px solid transparent',
                  transition:'background 0.12s',
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.015)'; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:12,fontWeight:700,color:'#e0e8f0'}}>{t.baseToken?.symbol || '?'}</span>
                    <span style={{fontSize:9,color:'#3a4a5a',background:'#0d1117',padding:'1px 5px',borderRadius:3,border:'1px solid #1a2030'}}>{age}</span>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color: chg1h >= 0 ? '#00e5cc' : '#ff6b6b'}}>
                    {chg1h >= 0 ? '+' : ''}{chg1h.toFixed(1)}%
                  </span>
                </div>
                <div style={{fontSize:10,color:'#5a6a80',marginBottom:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {t.baseToken?.name || '—'}
                </div>
                <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                  <span style={{fontSize:10,color:'#3a4a5a'}}>Liq : <span style={{color:'#7c4dff',fontWeight:600}}>{fmtUsd(liq)}</span></span>
                  <span style={{fontSize:10,color:'#3a4a5a'}}>Vol 24h : <span style={{color:'#5a6a80'}}>{fmtUsd(vol)}</span></span>
                  <span style={{fontSize:10,color:'#3a4a5a'}}>
                    <span style={{color:'#00e5cc'}}>↑{buys24h}</span>
                    <span style={{color:'#3a4a5a'}}> / </span>
                    <span style={{color:'#ff6b6b'}}>↓{sells24h}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── COLONNE DROITE : acheteurs ── */}
      <div style={S.right}>
        {!selected ? (
          <div style={S.emptyState}>
            <div style={{fontSize:40}}>🎯</div>
            <div style={{fontSize:13,fontWeight:600,color:'#5a6a80'}}>Clique sur un token</div>
            <div style={{fontSize:11,color:'#3a4a5a'}}>pour voir les plus gros acheteurs</div>
          </div>
        ) : (
          <>
            {/* Header token sélectionné */}
            <div style={{padding:'12px 20px',borderBottom:'1px solid #1a2030',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                <div>
                  <span style={{fontSize:14,fontWeight:700,color:'#e0e8f0'}}>{selected.baseToken?.symbol}</span>
                  <span style={{fontSize:11,color:'#5a6a80',marginLeft:8}}>{selected.baseToken?.name}</span>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginTop:2}}>
                    <span style={{fontSize:10,color:'#3a4a5a',fontFamily:'monospace'}}>
                      {selected.baseToken?.address?.slice(0,16)}...{selected.baseToken?.address?.slice(-8)}
                    </span>
                    <button
                      onClick={() => copyAddr(selected.baseToken?.address)}
                      title="Copier l'adresse du token"
                      style={{
                        background: copied === selected.baseToken?.address ? 'rgba(0,229,204,0.1)' : 'rgba(90,106,128,0.08)',
                        border: '1px solid #1a2030', borderRadius: 4,
                        color: copied === selected.baseToken?.address ? '#00e5cc' : '#5a6a80',
                        fontSize: 10, padding: '2px 8px', cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      {copied === selected.baseToken?.address ? '✓ Copié' : '⎘ Adresse'}
                    </button>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:10,color:'#5a6a80'}}>$ min investi :</span>
                  <input
                    type="number" value={Math.round(minSol * solPrice)}
                    onChange={e => setMinSol(Number(e.target.value) / solPrice)}
                    style={{...S.inp, width:65}}
                  />
                  <button onClick={loadBuyers} disabled={buyLoad} style={{
                    background:'linear-gradient(135deg,rgba(240,165,0,0.15),rgba(255,107,0,0.1))',
                    border:'1px solid rgba(240,165,0,0.3)',
                    borderRadius:6, color:'#f0a500',
                    fontSize:11, padding:'5px 14px', cursor:'pointer', fontWeight:700,
                  }}>
                    {buyLoad ? '⏳ Chargement...' : '🔍 Voir les acheteurs'}
                  </button>
                </div>
              </div>
            </div>

            {/* Graphique DexScreener */}
            <div style={{height:320,flexShrink:0,borderBottom:'1px solid #1a2030',background:'#060809'}}>
              <iframe
                key={selected.pairAddress}
                src={'https://dexscreener.com/solana/' + selected.pairAddress + '?embed=1&theme=dark&trades=0&info=0'}
                style={{width:'100%',height:'100%',border:'none'}}
                title="chart"
                allow="clipboard-write"
              />
            </div>

            {/* Liste acheteurs */}
            <div style={{flex:1,overflowY:'auto',padding:'0 20px 20px'}}>
              {!buyLoad && !buyError && buyers.length === 0 && (
                <div style={{padding:40,textAlign:'center',color:'#3a4a5a'}}>
                  <div style={{fontSize:28,marginBottom:12}}>🔍</div>
                  <div style={{fontSize:12,marginBottom:16}}>Clique sur <b style={{color:'#f0a500'}}>"Voir les acheteurs"</b><br/>pour charger les gros wallets</div>
                  <div style={{fontSize:10,color:'#2a3040'}}>100 crédits Helius par clic</div>
                </div>
              )}
              {buyLoad ? (
                <div style={{padding:40,textAlign:'center',color:'#3a4a5a',fontSize:12}}>
                  <div style={{marginBottom:8,fontSize:20}}>⏳</div>
                  Analyse des transactions en cours...
                </div>
              ) : buyError ? (
                <div style={{margin:'16px 0',padding:16,background:'rgba(255,107,107,0.05)',border:'1px solid #ff6b6b22',borderRadius:8,color:'#ff6b6b',fontSize:12,textAlign:'center'}}>
                  ⚠ {buyError}
                </div>
              ) : buyers.length === 0 ? (
                <div style={{padding:40,textAlign:'center',color:'#3a4a5a',fontSize:12}}>
                  Aucun acheteur ≥ {fmtUsd(minSol * solPrice)} trouvé<br/>
                  <span style={{fontSize:10}}>Essaie de baisser le SOL minimum</span>
                </div>
              ) : (
                <>
                  <div style={{padding:'12px 0 10px',fontSize:11,color:'#5a6a80',fontWeight:600}}>
                    {buyers.length} acheteur{buyers.length > 1 ? 's' : ''} ≥ {fmtUsd(minSol * solPrice)} — triés par montant investi
                  </div>
                  {buyers.map((b, i) => (
                    <div
                      key={b.wallet}
                      style={{
                        background:'#0b0e14', border:'1px solid #1a2030', borderRadius:8,
                        padding:'12px 14px', marginBottom:8,
                        display:'flex', alignItems:'center', gap:12,
                      }}
                    >
                      {/* Rang */}
                      <div style={{
                        width:30,height:30,borderRadius:'50%',flexShrink:0,
                        background: i === 0 ? 'linear-gradient(135deg,#f0a500,#ff6b00)'
                                  : i === 1 ? 'linear-gradient(135deg,#9ea7b3,#5a6a80)'
                                  : i === 2 ? 'linear-gradient(135deg,#cd7f32,#8b4500)'
                                  : 'linear-gradient(135deg,#1a2030,#0d1117)',
                        display:'flex',alignItems:'center',justifyContent:'center',
                        fontSize:11,fontWeight:800,color:'#e0e8f0',
                        border:'1px solid #2a3040',
                      }}>
                        {i + 1}
                      </div>

                      {/* Infos wallet */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontFamily:'monospace',color:'#e0e8f0',marginBottom:4}}>
                          {b.wallet.slice(0,8)}...{b.wallet.slice(-6)}
                        </div>
                        <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                          <span style={{fontSize:10,color:'#3a4a5a'}}>
                            Investi : <span style={{color:'#f0a500',fontWeight:700}}>{fmtUsd(b.totalSol * solPrice)}</span>
                          </span>
                          <span style={{fontSize:10,color:'#3a4a5a'}}>
                            Trades : <span style={{color:'#7c4dff'}}>{b.trades}</span>
                          </span>
                          <span style={{fontSize:10,color:'#3a4a5a'}}>
                            1er achat : <span style={{color:'#5a6a80'}}>il y a {timeAgo(b.firstSeen * 1000)}</span>
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{display:'flex',gap:6,flexShrink:0}}>
                        <button
                          onClick={() => copyAddr(b.wallet)}
                          title="Copier l'adresse"
                          style={{
                            background: copied === b.wallet ? 'rgba(0,229,204,0.1)' : 'rgba(90,106,128,0.08)',
                            border:'1px solid #1a2030', borderRadius:5,
                            color: copied === b.wallet ? '#00e5cc' : '#5a6a80',
                            fontSize:11, padding:'5px 9px', cursor:'pointer',
                          }}
                        >
                          {copied === b.wallet ? '✓' : '⎘'}
                        </button>
                        <button
                          onClick={() => onAnalyzeWallet(b.wallet)}
                          style={{
                            background:'linear-gradient(135deg,rgba(0,229,204,0.1),rgba(124,77,255,0.1))',
                            border:'1px solid rgba(0,229,204,0.25)',
                            borderRadius:5, color:'#00e5cc',
                            fontSize:11, padding:'5px 12px', cursor:'pointer', fontWeight:600,
                          }}
                        >
                          Analyser →
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
